import React, {
  useState,
  useEffect,
  createRef,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styles from "./../../../../Scheduler.module.scss";
import * as moment from "moment";
import { encodeId, generateMonth, generateWeek } from "utils";
import { isMobileTab } from "utils";
import toast from "react-hot-toast";
import { withTranslation } from "react-i18next";
import Loader from "components/Loader";
import constants from "../../../../../../../constants.js";
import { useHistory } from "react-router-dom";
import MoveToApp from "accountOwner/pages/Scheduler/components/Modals/MoveToApp";
import HolidayImage from "assets/images/early_on.svg";
import { ThreeDots } from "react-bootstrap-icons";
import {
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import SelectOfficeModal from "accountOwner/pages/Scheduler/components/Modals/SelectOfficeModal";
import {
  MoreTagsFunction,
  renderOfficeNameOrEnterprise,
} from "accountOwner/pages/AddEvent/addEvents.functions";
import InviteesPopup from "accountOwner/pages/EventDetails/components/Modals/InviteesPopup";
import { leaveClasses } from "accountOwner/pages/Scheduler/scheduler.function";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { showTime } from "accountOwner/pages/Scheduler/OfficeSchedulerTab/SubTabs/EventsShiftsTab";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import {
  splitAllDayAndTimedEvents,
  isAgendaEmptyData,
} from "./agendaView.helpers";

function AgendaWeeklyView({
  t,
  isSidebarActive,
  currentDate,
  isWeekly,
  currentUserId,
  isFromDashBoard,
  viewSettingFilter,
  setDateRange,
  agenda,
  isLoading,
  isFetching,
  error,
  selectedOwnerId,
  isStaff,
  canShowOfficeName,
}) {
  const calendarRef = createRef(null);
  const { profile, effectiveOwnerIdString } = useAccountOwnerCommon();
  const [weekData, setWeekData] = useState([]);
  const [monthData, setMonthData] = useState([]);
  const [isModal, setIsModal] = useState({ open: false });
  const [showOfficeSelectModal, setShowOfficeSelectModal] = useState(false);
  const [SelectedDateForLeave, setSelectedDateForLeave] = useState(null);
  const [hoverBusySlotKey, setHoverBusySlotKey] = useState("");
  // Added state for dynamic height
  const [dynamicHeight, setDynamicHeight] = useState("calc(100vh - 200px)");
  const [invitePopupPosition, setInvitePopupPosition] = useState({
    top: 0,
    right: 0,
  });
  const handlePopupToggle1 = (event, hoverbusykey = "") => {
    event.stopPropagation();
    setHoverBusySlotKey(hoverbusykey);
    setInvitePopupPosition({
      top: 85,
      right: 0,
    });
  };
  const scrollWidth = 125;
  const history = useHistory();
  // Added ref for the container
  const containerRef = useRef(null);

  useEffect(() => {
    getWeeksDates(currentDate);
    getMonthDates(currentDate);
    if (isMobileTab() && isSidebarActive) {
      toast.success(t("accountOwner.dateApply"));
    }
    // eslint-disable-next-line
  }, [currentDate, isWeekly]);
  const { search } = useLocation();
  const dateMatchForHighlight = useMemo(() => {
    const searchValue = new URLSearchParams(search).get(showTime);
    if (searchValue?.includes("NaN") || !search) {
      return null;
    }
    return new Date(+searchValue);
  }, [search]);

  // --- Dynamic Height Calculation Logic Start ---
  useEffect(() => {
    // Skip calculation if on dashboard (as it has fixed style requirements)
    if (isFromDashBoard) return;

    const handleResize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Calculate available space: Window Height - Top Position - Buffer (30px)
        const availableHeight = window.innerHeight - rect.top - 30;

        // Calculate height with constraints:
        // 1. Minimum 300px
        // 2. Maximum 1450px (capped)
        const calculatedHeight = Math.min(1450, Math.max(300, availableHeight));

        setDynamicHeight(`${calculatedHeight}px`);
      }
    };

    // Calculate initially
    handleResize();

    // Add event listeners for BOTH resize and scroll to update height dynamically
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize);
    };
  }, [isFromDashBoard, agenda, weekData, monthData]);
  // --- Dynamic Height Calculation Logic End ---

  const getWeeksDates = (_currentDate) => {
    const selectedStartWeek = moment(_currentDate).startOf("isoWeek");
    const selectedEndWeek = moment(_currentDate).endOf("isoWeek");
    const currentweekData = generateWeek(selectedStartWeek, selectedEndWeek);
    if (isWeekly) {
      setDateRange({
        startDate: moment(selectedStartWeek).format("YYYY-MM-DD"),
        endDate: moment(selectedEndWeek).format("YYYY-MM-DD"),
      });
    }
    setWeekData(currentweekData);
  };

  const getMonthDates = (_currentDate) => {
    const monthDates = generateMonth(_currentDate);
    const monthLength = monthDates.length;
    if (!isWeekly) {
      setDateRange({
        startDate: moment(monthDates[0].date).format("YYYY-MM-DD"),
        endDate: moment(monthDates[monthLength - 1].date).format("YYYY-MM-DD"),
      });
    }
    setMonthData(monthDates);
  };

  useEffect(() => {
    if (!isLoading && !isFetching && error?.message) {
      toast.error(error.message);
    }
    //eslint-disable-next-line
  }, [error]);

  const isPastDate = (date) => {
    return moment(date, "YYYY-MM-DD").isBefore(moment(), "day");
  };

  const tableWeekDates = weekData.map((v, i) => (
    <th
      key={i}
      className={`${
        styles.custom_th + " " + " p-0" + " " + styles.custom_th_dots
      } ${isFromDashBoard ? styles.dashboard_th : ""}`}
    >
      <div className={styles.weekly_agenda_header}>
        {v.mDate}
        {!isPastDate(v?.date?.format("YYYY-MM-DDTHH:mm:ss")) && (
          <UncontrolledDropdown className={styles.ellipsis_position_dots}>
            <DropdownToggle caret={false} tag="div" className="cursor-pointer">
              <div className={styles.ellipsis_icon + " " + `ellipsis-icon`}>
                <ThreeDots />
              </div>
            </DropdownToggle>

            <DropdownMenu end className={styles["office-dropdown-menu"]}>
              {isStaff && (
                <DropdownItem
                  className={styles["office-dropdown-item"]}
                  onClick={() => {
                    setShowOfficeSelectModal(true);
                    setSelectedDateForLeave(currentDate);
                  }}
                >
                  {t("staffLeaves.applyLeave")}
                </DropdownItem>
              )}
              <DropdownItem
                className={styles["office-dropdown-item"]}
                onClick={() => {
                  history.push({
                    pathname: constants.routes.scheduler.addBusySlot,
                    state: {
                      date: currentDate,
                    },
                  });
                }}
              >
                {t("accountOwner.addBusyslots")}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        )}
      </div>
    </th>
  ));

  const tableMonthDates = monthData.map((v, i) => (
    <th
      key={i}
      className={styles["height-60"]}
      style={
        isFromDashBoard
          ? {
              verticalAlign: "middle",
              textAlign: "center",
              backgroundColor: "#F8F9F7",
              whiteSpace: "nowrap",
              borderLeft: "none",
            }
          : {
              verticalAlign: "middle",
              textAlign: "center",
              backgroundColor: "#F8F9F7",
              whiteSpace: "nowrap",
            }
      }
    >
      {v.mDate}
    </th>
  ));

  const goToDetails = (id, type, createdByMe, isInvite, requestedDate) => {
    if (!id || !type) return;
    switch (type) {
      case constants.agendaType.EVENT: {
        let pathname = "/";
        if (createdByMe) {
          pathname = constants.routes.scheduler.eventDetails;
        } else if (isInvite) {
          pathname = constants.routes.scheduler.eventRequestDetails;
        } else {
          pathname = constants.routes.scheduler.EventWorkingDetails;
        }

        history.push({
          pathname: pathname.replace(":eventId", encodeId(id)),
          state: { requestedDate },
          search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
        });

        break;
      }
      case constants.agendaType.BUSY_SLOT: {
        const pathname = constants.routes.scheduler.busySlotDetail.replace(
          ":busySlotId",
          encodeId(id),
        );
        history.push({
          pathname,
          state: { requestedDate },
          search: effectiveOwnerIdString ? `?${effectiveOwnerIdString}` : "",
        });
        break;
      }
      default: {
        return;
      }
    }
  };
  const viewTitleAndotherKeys = useCallback(
    (data, isFromBusySlot = false, officeName = [], thisHoverKey = "") => {
      return (
        <>
          {(!viewSettingFilter.length ||
            viewSettingFilter.includes("timeString")) && (
            <div className={styles["event-date"]}>
              {data?.timeString}
              {!isFromBusySlot &&
                ` ${data?.timezoneCode ? `(${data?.timezoneCode})` : ""}`}
            </div>
          )}

          {canShowOfficeName &&
            (!viewSettingFilter.length ||
              viewSettingFilter.includes("officeName")) && (
              <div className={styles["event-office"]}>
                {isFromBusySlot ? (
                  <MoreTagsFunction
                    value={officeName || []}
                    lastIndex={2}
                    clickHandler={(e) => handlePopupToggle1(e, thisHoverKey)}
                    mapFn={(member) => `${member?.name}`}
                  />
                ) : (
                  <>{data.officeName}</>
                )}
              </div>
            )}
          {!viewSettingFilter.length && data?.location && (
            <div className={styles["event-office"]}>
              <img
                src={require("assets/images/pin_drop_black.svg").default}
                alt="caret"
              />
              <span className="ml-1 break_all ">{data.location}</span>
            </div>
          )}
          {(!viewSettingFilter.length || viewSettingFilter.includes("title")) &&
            (data?.isShift ? (
              viewSettingFilter.includes("title") && (
                <div className={styles["event-type"]}>
                  {data.title?.trim()
                    ? data.title
                    : t("scheduler.agenda.shiftTag")}
                </div>
              )
            ) : (
              <div className={styles["event-type"]}> {data.title} </div>
            ))}
        </>
      );
    },
    [viewSettingFilter, t, canShowOfficeName],
  );
  const getUsefulInfo = useCallback((item) => {
    const id = item?.id;
    const officeName = renderOfficeNameOrEnterprise(item, t);
    let title = item?.title || item?.reasonForBlock || null;
    title =
      title && title.length > constants.agendaTitleLength
        ? title.slice(0, constants.agendaTitleLength).concat(" ...")
        : title;
    const timezoneCode = item?.timezoneCode || null;
    const isAllDayEvent = item?.isAllDayEvent;
    const timeString = item?.isAllDayEvent
      ? t("scheduler.agenda.allDayEvent")
      : moment(item?.startTime).format("h:mm A") +
        " - " +
        moment(item?.endTime).format("h:mm A");
    //sepecific to Schedular events
    const createdByMe = item.createdById === currentUserId;
    const isInvite = !!item.eventEmployeeStatus;
    const status = item.eventEmployeeStatus || item.eventRequestToJoinStatus;
    const patientName = item.patientFullName;
    const location = item?.location;
    let backgroudColorClass;
    backgroudColorClass = createdByMe
      ? ""
      : status === constants.SCHEDULERSTATUS.PENDING
        ? "color-blue"
        : "color-grey";
    return {
      officeName,
      title,
      timezoneCode,
      timeString,
      id,
      isAllDayEvent,
      createdByMe,
      isInvite,
      status,
      backgroudColorClass,
      patientName,
      location,
      isShift: item?.isShift,
    };
  }, []);
  const getAppointmentsView = (item, index) => {
    const data = getUsefulInfo(item);
    data.title = t("scheduler.agenda.appointmentTitle");

    return (
      <div
        key={index}
        className={
          styles["scheduler-event-box"] + " " + styles["light-purple-bg"]
        }
        onClick={() => {
          setIsModal({ open: true, type: constants.agendaType.APPOINTMENT });
        }}
      >
        <div className={styles["event-tag"] + " " + styles["dark-grey-bg"]}>
          {t("scheduler.agenda.appointmentTag")}
        </div>
        {data?.patientName && (
          <div className={styles["event-office"]}>{data?.patientName}</div>
        )}
        {viewTitleAndotherKeys(data)}
      </div>
    );
  };
  const getSchedulerEventsView = (item, index, requestedDate) => {
    const data = getUsefulInfo(item);
    return (
      <div
        key={index}
        className={
          styles["scheduler-event-box"] +
          " " +
          styles[data.backgroudColorClass] +
          " cursor-pointer "
        }
        onClick={() =>
          goToDetails(
            data.id,
            constants.agendaType.EVENT,
            data.createdByMe,
            data.isInvite,
            requestedDate,
          )
        }
      >
        <div className={styles["event-tag"] + " " + styles["theme-green-bg"]}>
          {t("scheduler.agenda.eventTag")}
        </div>
        {viewTitleAndotherKeys(data)}
      </div>
    );
  };
  const getSchedulerBusySlotsView = (
    item,
    index,
    shouldRedirect,
    requestedDate,
  ) => {
    const data = getUsefulInfo(item);
    const searchKey = `${item?.id}_${requestedDate}`;
    const thisHoverKey = `${searchKey}_${index}`;
    let officeName = data?.officeName?.split(",")?.map((item) => {
      return {
        name: item,
        id: item,
      };
    });
    let isFromBusySlot = true;
    return (
      <div
        key={index}
        className={
          styles["scheduler-event-box"] +
          " " +
          styles["light-orange-bg"] +
          " cursor-pointer position-relative"
        }
        onClick={() => {
          if (shouldRedirect) {
            goToDetails(
              data.id,
              constants.agendaType.BUSY_SLOT,
              null,
              null,
              requestedDate,
            );
          } else {
            setIsModal({
              open: true,
              type: constants.agendaType.BLOCKED,
              officeId: item?.officeId,
            });
          }
        }}
      >
        <div className={styles["event-tag"] + " " + styles["dark-orange-bg"]}>
          {t("scheduler.agenda.busyTag")}
        </div>
        {viewTitleAndotherKeys(data, isFromBusySlot, officeName, thisHoverKey)}

        {canShowOfficeName && thisHoverKey == hoverBusySlotKey && (
          <InviteesPopup
            position={invitePopupPosition}
            handleInvitePopupToggle={(e) => handlePopupToggle1(e)}
            list={officeName}
            showRoleAndOffice={false}
            isForEvent={true}
            isfromBusySlot={true}
            onlyOfffice
          />
        )}
      </div>
    );
  };

  const getLeavesView = (item, index) => {
    const data = getUsefulInfo(item);
    data.timeString = "";
    data.timezoneCode = "";
    data.title = "";
    const { Highlighter, main, text } = leaveClasses(item?.statusId);

    return (
      <div
        key={index}
        className={styles["scheduler-event-box"] + " " + styles[main]}
        onClick={() => {
          const isBeforeToday = moment(item?.startDate).isBefore(
            moment().startOf("day"),
          );
          if (item?.startDate && !isBeforeToday) {
            history.push({
              pathname: constants.routes.staff.editLeaves.replace(
                ":officeId",
                encodeId(item.officeId),
              ),
              state: { ...item, userId: profile.id, backRedirect: true },
            });
          } else if (isBeforeToday) {
            toast.error("You cannot edit the leaves for past dates");
          }
        }}
      >
        <div className={styles["event-tag"] + " " + styles[Highlighter]}>
          {t(text)}
        </div>
        {viewTitleAndotherKeys(data)}
      </div>
    );
  };

  const divRef = useRef(null);
  let filteredAgenda = agenda;
  let tableData = null;
  let holidayRowJsx = null;
  if (filteredAgenda?.length) {
    const agendaFilter = filteredAgenda;

    tableData = filteredAgenda.map((_item) => {
      let {
        requestedDate,
        appointments,
        leaves,
        schedulerBusySlots,
        schedulerEvents,
        appointmentSlots,
        holidayList,
      } = _item;
      appointments = appointments || [];
      leaves = leaves || [];
      schedulerBusySlots = schedulerBusySlots || [];
      schedulerEvents = schedulerEvents || [];
      appointmentSlots = appointmentSlots || [];
      const [allDayEvents, allOtherEvents] =
        splitAllDayAndTimedEvents(schedulerEvents);

      let allItems = [
        appointments,
        schedulerBusySlots,
        allOtherEvents,
        appointmentSlots,
      ]
        .flat()
        .sort((a, b) => {
          if (
            moment(moment(b.startTime).format("HH:mm"), "HH:mm").isAfter(
              moment(moment(a.startTime).format("HH:mm"), "HH:mm"),
            )
          ) {
            return -1;
          } else {
            return 1;
          }
        });
      allItems = [...leaves, ...allDayEvents, ...allItems];
      const highlightCheck =
        dateMatchForHighlight &&
        moment(dateMatchForHighlight)
          .startOf("day")
          .isSame(moment(requestedDate).startOf("day"));
      return (
        <td
          key={requestedDate}
          ref={highlightCheck ? divRef : undefined}
          className={`${holidayList?.length > 0 ? styles["light-red-bg"] : ""}${
            highlightCheck ? " blinking-background " : ""
          }`}
          style={
            isFromDashBoard
              ? { height: "100%", borderLeft: "none" }
              : { height: "100%", width: "14.28571%" }
          }
        >
          {allItems.map((item, index) => {
            const { agendaType } = item;
            switch (agendaType) {
              case constants.agendaType.EVENT: {
                return getSchedulerEventsView(item, index, requestedDate);
              }
              case constants.agendaType.BUSY_SLOT:
              case constants.agendaType.BLOCKED: {
                return getSchedulerBusySlotsView(
                  item,
                  index,
                  agendaType === constants.agendaType.BUSY_SLOT,
                  requestedDate,
                );
              }
              case constants.agendaType.APPOINTMENT: {
                return getAppointmentsView(item, index);
              }
              case constants.agendaType.LEAVE: {
                return getLeavesView(item, index);
              }
              default: {
                return null;
              }
            }
          })}
          {isAgendaEmptyData(filteredAgenda) && (
            <div className={styles["empty-event"]}></div>
          )}
        </td>
      );
    });
    holidayRowJsx = (
      <tr>
        {agendaFilter.map((date, index) => {
          const adjustedIndex = index;
          const validIndex = agendaFilter[adjustedIndex];

          const holidayList = validIndex
            ? agendaFilter[adjustedIndex].holidayList
            : null;

          if (
            holidayList?.length > 0 &&
            validIndex?.requestedDate === holidayList?.[0]?.date
          ) {
            return (
              <td
                key={`holiday-${index}`}
                className={styles["holiday-cell"] + " p-0"}
              >
                <div className={styles["holiday_title"]}>
                  <img
                    src={HolidayImage}
                    alt="holiday"
                    className="account_owner_image"
                  />
                  <div className={styles.holiday_title_text}>
                    {holidayList?.[0]?.title}
                  </div>
                </div>
              </td>
            );
          }
          return (
            <td
              key={`holiday-${index}`}
              style={isFromDashBoard ? { borderLeft: "none" } : {}}
            ></td>
          );
        })}
      </tr>
    );
  }

  useEffect(() => {
    if (currentDate && calendarRef?.current) {
      if (!isWeekly) {
        calendarRef.current.scrollLeft =
          scrollWidth * (moment(currentDate).date() - 1);
      } else {
        calendarRef.current.scrollLeft =
          scrollWidth * (moment(currentDate).isoWeekday() - 1);
      }
    }
    //eslint-disable-next-line
  }, [currentDate, isWeekly, agenda, weekData, monthData]);

  useEffect(() => {
    if (!search || !tableData || !agenda?.length) return;
    if (divRef.current) {
      setTimeout(() => {
        divRef.current.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "center",
        });
      }, 500);
    }

    const timeOut = setTimeout(() => {
      const queryParams = new URLSearchParams(search);
      if (queryParams.has(showTime)) {
        queryParams.delete(showTime);
        history.replace({
          search: queryParams.toString(),
        });
      }
    }, 3000);

    return () => {
      clearTimeout(timeOut);
    };
  }, [search, tableData]);

  return (
    <div
      ref={containerRef}
      className={"table-card-border agenda-fixed-table"}
      style={
        isFromDashBoard
          ? { height: "100%", borderLeft: "none" }
          : { height: dynamicHeight, overflow: "auto" }
      }
    >
      {isLoading && <Loader />}
      <div
        className={
          styles["scheduler-table-wrapper"] +
          " " +
          styles["fixed-header-table-wrapper"]
        }
      >
        <div
          ref={calendarRef}
          className={
            isFromDashBoard
              ? styles["scheduler-fixed-table-new-my-schedule"]
              : `${styles["scheduler-fixed-table"]} ${styles["AgendaWeekly-table-fix"]}`
          }
        >
          <table
            className={
              "table table-bordered " +
              styles["schedular-table"] +
              " " +
              styles["fixed-header-table"] +
              " " +
              styles["agenda-weekly-table"]
            }
            style={
              isFromDashBoard
                ? { minHeight: "408px", borderLeft: "none" }
                : { height: "100%" }
            }
          >
            <thead>
              <tr className="weekly-table-background">
                {isWeekly ? tableWeekDates : tableMonthDates}
              </tr>
            </thead>
            <tbody>
              {holidayRowJsx}
              <tr>
                {tableData}
                <MoveToApp
                  isModal={isModal?.open}
                  openModal={setIsModal}
                  type={isModal?.type}
                  officeId={isModal?.officeId || null}
                />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {showOfficeSelectModal && (
        <SelectOfficeModal
          selectedOwnerId={selectedOwnerId}
          showOfficeSelectModal={showOfficeSelectModal}
          setShowOfficeSelectModal={setShowOfficeSelectModal}
          SelectedDateForLeave={SelectedDateForLeave}
        />
      )}
    </div>
  );
}

export default withTranslation()(AgendaWeeklyView);
